import { PLATFORM } from "@/constants/jobConstants";
import { PUBLISHING } from "@/constants/commonConstants";
import { filterSupportedProfileChannels } from "@/utilities/jobs/profileUtils";
import { ProfileChannel, ProfileData, ProfilePublish, ProfilePublishValues } from "@/types/profile";

export type ProfilePlatform = typeof PLATFORM.WINDOWS | typeof PLATFORM.MAC | typeof PLATFORM.LINUX;

export interface AllowedJobTypes {
  di: boolean;
  db: boolean;
  ds: boolean;
  dbPreBuilt: boolean;
}

export interface ProfileImageInfoConstraints {
  isActive: boolean;
  allowedPlatforms: ProfilePlatform[];
  /** null = user may choose (unconstrained) */
  odisOnboarded: boolean | null;
  allowInstall: boolean | null;
  allowDownload: boolean | null;
  /**
   * Single build type ("di" | "db" | "ds") resolved from the selected channel's Publish.JobType,
   * for publishing types whose build type is otherwise hardcoded (see NON_BUNDLE_PUBLISHING_TYPES).
   * null when the channel doesn't unambiguously specify one type (falls back to the hardcoded default).
   */
  channelJobType: "di" | "db" | "ds" | null;
  /**
   * The channel's Publish.JobType serialized for the submit-job store (see serializeProfileJobTypes);
   * "" when the profile doesn't constrain Install / Download / Component Path.
   */
  profileJobTypes: string;
}

export interface DeriveImageInfoConstraintsArgs {
  profileData?: ProfileData;
  apiChannel?: string;
  publishing?: string;
  editJobId?: string;
  /** Uploaded YAML is the source of truth for Image Info, so the profile must not pre-fill it. */
  hasYaml?: boolean;
}

export interface SubmitJobImageInfoState {
  platform?: string;
  odisOnboarded?: boolean;
  productDISelect?: boolean;
  productDBSelect?: boolean;
}

export interface EffectiveChannel extends ProfileChannel {
  Publish?: ProfilePublish;
}

export const ALL_PLATFORMS: ProfilePlatform[] = [PLATFORM.WINDOWS, PLATFORM.MAC, PLATFORM.LINUX];

/**
 * Publishing types whose build (Output.Type) is otherwise hardcoded to "ds" in
 * getJobImageTypeFromData(), regardless of the selected profile/channel. Kept lowercase to match
 * that switch statement's cases (see src/utilities/jobs/submitJobUtils.ts).
 */
export const NON_BUNDLE_PUBLISHING_TYPES = ["language pack", "library", "update", "plug-in", "engine", "extension"];

/**
 * Publishing types whose Install (di) / Download (db) checkboxes and Component Path (ds, or db PreBuilt) are
 * enabled by the selected channel's Publish.JobType. File types (Badge/Icon, File, Component Update) have no image.
 */
export const PROFILE_IMAGE_PUBLISHING_TYPES = [
  PUBLISHING.PRODUCT,
  PUBLISHING.PRODUCT_MAJOR_UPGRADE,
  PUBLISHING.COMPONENT,
  PUBLISHING.UPDATE,
  PUBLISHING.EXTENSION,
  PUBLISHING.LIBRARY,
  PUBLISHING.ENGINE,
  PUBLISHING.LANGUAGE_PACK,
  PUBLISHING.PLUG_IN
];

export const isProfileImagePublishing = (publishing: string | undefined): boolean =>
  PROFILE_IMAGE_PUBLISHING_TYPES.includes(String(publishing || ""));

export const UNCONSTRAINED_IMAGE_INFO: ProfileImageInfoConstraints = {
  isActive: false,
  allowedPlatforms: ALL_PLATFORMS,
  odisOnboarded: null,
  allowInstall: null,
  allowDownload: null,
  channelJobType: null,
  profileJobTypes: ""
};

const PROFILE_PLATFORM_BY_KEY: Record<string, ProfilePlatform> = {
  windows: PLATFORM.WINDOWS,
  mac: PLATFORM.MAC,
  linux: PLATFORM.LINUX
};

export const normalizeProfileConstraintPlatform = (value: string | undefined): ProfilePlatform | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  if ((ALL_PLATFORMS as readonly string[]).includes(trimmed)) {
    return trimmed as ProfilePlatform;
  }

  return PROFILE_PLATFORM_BY_KEY[trimmed.toLowerCase()];
};

/** Profile and submit-job platform values both use lowercase `PLATFORM.*` strings. */
export const toSubmitJobPlatformValue = (platform: ProfilePlatform): string => platform;

const normalizePlatform = normalizeProfileConstraintPlatform;

export const normalizeProfilePlatforms = (platforms: string[] | undefined): ProfilePlatform[] => {
  if (!platforms?.length) return ALL_PLATFORMS;
  const normalized = platforms.map((platform) => normalizePlatform(platform)).filter(Boolean) as ProfilePlatform[];
  return normalized.length ? normalized : ALL_PLATFORMS;
};

export const resolveEffectiveChannel = (
  channels: ProfileChannel[] | undefined,
  apiChannel: string | undefined
): EffectiveChannel | undefined => {
  if (!channels?.length || !apiChannel) return undefined;
  return filterSupportedProfileChannels(channels).find((channel) => channel.Name === apiChannel);
};

export const parseAllowedJobTypes = (jobType: ProfilePublishValues | undefined): AllowedJobTypes => {
  const empty: AllowedJobTypes = { di: false, db: false, ds: false, dbPreBuilt: false };
  if (!jobType) return empty;

  if (Array.isArray(jobType)) {
    const normalized = jobType.map((value) => String(value).toLowerCase());
    return {
      di: normalized.includes("di"),
      db: normalized.includes("db"),
      ds: normalized.includes("ds"),
      dbPreBuilt: false
    };
  }

  const keys = Object.keys(jobType).map((key) => key.toLowerCase());
  const dbValue = jobType.db ?? jobType.DB;

  return {
    di: keys.includes("di"),
    db: keys.includes("db"),
    ds: keys.includes("ds"),
    dbPreBuilt:
      typeof dbValue === "object" &&
      dbValue !== null &&
      "PreBuilt" in dbValue &&
      (dbValue as { PreBuilt?: boolean }).PreBuilt === true
  };
};

export type ChannelJobType = "di" | "db" | "ds";

/** Submit order for a channel's build types: Install job first, then Download, then Download-only. */
const CHANNEL_JOB_TYPES: ChannelJobType[] = ["di", "db", "ds"];

const DB_PREBUILT_TOKEN = "dbPreBuilt";

/** Store-friendly form of AllowedJobTypes, e.g. "di,db,dbPreBuilt"; "" when the channel lists no build types. */
export const serializeProfileJobTypes = (jobTypes: AllowedJobTypes): string =>
  [...CHANNEL_JOB_TYPES.filter((type) => jobTypes[type]), ...(jobTypes.dbPreBuilt ? [DB_PREBUILT_TOKEN] : [])].join(",");

/** Inverse of serializeProfileJobTypes; null when the profile doesn't constrain the job types. */
export const parseProfileJobTypes = (value: string | undefined): AllowedJobTypes | null => {
  const tokens = String(value || "").split(",").filter(Boolean);
  if (!tokens.length) return null;
  return {
    di: tokens.includes("di"),
    db: tokens.includes("db"),
    ds: tokens.includes("ds"),
    dbPreBuilt: tokens.includes(DB_PREBUILT_TOKEN)
  };
};

/** Install / Download are offered on every platform, each enabled only when the channel lists it. */
const deriveCheckboxConstraints = (
  publishing: string | undefined,
  jobTypes: AllowedJobTypes
): Pick<ProfileImageInfoConstraints, "allowInstall" | "allowDownload"> => {
  const hasExplicitJobTypes = jobTypes.di || jobTypes.db || jobTypes.ds;
  if (!hasExplicitJobTypes || !isProfileImagePublishing(publishing)) {
    return { allowInstall: null, allowDownload: null };
  }
  return { allowInstall: jobTypes.di, allowDownload: jobTypes.db };
};

export const deriveImageInfoConstraints = ({
  profileData,
  apiChannel,
  publishing,
  editJobId,
  hasYaml
}: DeriveImageInfoConstraintsArgs): ProfileImageInfoConstraints => {
  if (editJobId || hasYaml || !profileData || !apiChannel) {
    return UNCONSTRAINED_IMAGE_INFO;
  }

  const allowedPlatforms = normalizeProfilePlatforms(profileData.Profile?.Platform);
  const effectiveChannel = resolveEffectiveChannel(profileData.Channels, apiChannel);
  const jobTypes = parseAllowedJobTypes(effectiveChannel?.Publish?.JobType);

  const isProduct = publishing === PUBLISHING.PRODUCT;

  // ODIS onboarding is no longer asked; it is implied by the channel (ds-only = not onboarded) and still
  // drives Product source-path rules. Derived on every platform so a value left by an earlier profile can't linger.
  let odisOnboarded: boolean | null = null;
  if (isProduct) {
    const hasDiDb = jobTypes.di || jobTypes.db;
    const hasDsOnly = jobTypes.ds && !hasDiDb;

    if (hasDsOnly) {
      odisOnboarded = false;
    } else if (hasDiDb) {
      odisOnboarded = true;
    }
  }

  const checkboxConstraints = deriveCheckboxConstraints(publishing, jobTypes);

  let channelJobType: "di" | "db" | "ds" | null = null;
  if (NON_BUNDLE_PUBLISHING_TYPES.includes((publishing ?? "").toLowerCase())) {
    const explicitTypes = CHANNEL_JOB_TYPES.filter((type) => jobTypes[type]);
    // Only override when the channel unambiguously specifies exactly one build type;
    // otherwise fall back to the hardcoded default so behavior is unchanged.
    if (explicitTypes.length === 1) {
      channelJobType = explicitTypes[0];
    }
  }

  return {
    isActive: true,
    allowedPlatforms,
    odisOnboarded,
    channelJobType,
    profileJobTypes: serializeProfileJobTypes(jobTypes),
    ...checkboxConstraints
  };
};

export interface ApplyImageInfoConstraintsOptions {
  /** When true, select both Install and Download if the channel allows both. */
  applyBothJobTypeDefaults?: boolean;
}

export const applyBothJobTypeDefaults = (
  constraints: ProfileImageInfoConstraints,
  current: SubmitJobImageInfoState
): Partial<SubmitJobImageInfoState> => {
  if (!constraints.isActive || constraints.allowInstall !== true || constraints.allowDownload !== true) {
    return {};
  }

  const patch: Partial<SubmitJobImageInfoState> = {};
  if (!current.productDISelect) patch.productDISelect = true;
  if (!current.productDBSelect) patch.productDBSelect = true;
  return patch;
};

export const getImageInfoPrefillKey = (
  profileName: string,
  apiChannel: string,
  publishing: string,
  constraints: ProfileImageInfoConstraints
): string => {
  if (!constraints.isActive) return "";
  return [
    profileName,
    apiChannel,
    publishing,
    constraints.allowedPlatforms.join(","),
    constraints.odisOnboarded,
    constraints.allowInstall,
    constraints.allowDownload
  ].join(":");
};

export const applyImageInfoConstraints = (
  current: SubmitJobImageInfoState,
  constraints: ProfileImageInfoConstraints,
  options?: ApplyImageInfoConstraintsOptions
): Partial<SubmitJobImageInfoState> => {
  if (!constraints.isActive) return {};

  const patch: Partial<SubmitJobImageInfoState> = {};
  const currentPlatform = normalizePlatform(current.platform);

  if (constraints.allowedPlatforms.length === 1) {
    if (currentPlatform !== constraints.allowedPlatforms[0]) {
      patch.platform = toSubmitJobPlatformValue(constraints.allowedPlatforms[0]);
    }
  } else if (!currentPlatform || !constraints.allowedPlatforms.includes(currentPlatform)) {
    patch.platform = toSubmitJobPlatformValue(constraints.allowedPlatforms[0]);
  }

  if (constraints.odisOnboarded !== null && current.odisOnboarded !== constraints.odisOnboarded) {
    patch.odisOnboarded = constraints.odisOnboarded;
  }

  const nextOdisOnboarded = patch.odisOnboarded ?? current.odisOnboarded;
  if (constraints.odisOnboarded === false || nextOdisOnboarded === false) {
    if (current.productDISelect) patch.productDISelect = false;
    if (current.productDBSelect) patch.productDBSelect = false;
    return patch;
  }

  if (constraints.allowInstall === false && current.productDISelect) {
    patch.productDISelect = false;
  }
  if (constraints.allowDownload === false && current.productDBSelect) {
    patch.productDBSelect = false;
  }

  const nextInstall = patch.productDISelect ?? current.productDISelect;
  const nextDownload = patch.productDBSelect ?? current.productDBSelect;

  if (constraints.allowInstall === true && constraints.allowDownload === false && !nextInstall) {
    patch.productDISelect = true;
  }
  if (constraints.allowDownload === true && constraints.allowInstall === false && !nextDownload) {
    patch.productDBSelect = true;
  }

  const merged: SubmitJobImageInfoState = {
    ...current,
    ...patch
  };

  if (options?.applyBothJobTypeDefaults) {
    Object.assign(patch, applyBothJobTypeDefaults(constraints, merged));
  }

  return patch;
};
